/** @format */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  compareDrawerOpened,
  compareProductDetailsFetchFailed,
  compareProductDetailsFetchRetryTriggered,
  compareProductDetailsFetchSucceeded,
  compareProductDetailsFetchTriggered,
  compareProductsAvailabilityFetched,
} from './compare.actions';
import { concatLatestFrom } from '@ngrx/operators';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import {
  selectCompareBarProducts,
  selectCompareProductDetails,
  selectIsCompareGridOpen,
  selectIsCompareHydrated,
} from './compare.selectors';
import { ProductCatalogService, SessionStorageService } from '@shared/data-access/services';
import {
  CompareBarProduct,
  HeaderStoreSelection,
  ProductAvailabilityCollection,
  CompareProductDetails,
} from '@shared/data-access/interfaces';
import { isPlatformServer } from '@angular/common';
import { COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, CompareService } from '@compare/data-access';
import { changeHeaderServiceStoreInfo, selectUserStoreInfo } from '@state/data-access';
import { CompareState } from '@plp/data-access/interfaces';
import { selectProductListSEOIdentifier } from '@plp/data-access/state';

@Injectable({ providedIn: 'root' })
export class CompareEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<CompareState>);
  private readonly productCatalogService = inject(ProductCatalogService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly compareService = inject(CompareService);

  private lastFetchParams: {
    ecodes: string[];
    isClearance: boolean;
  } | null = null;

  setCompareBarProducts$ = createEffect(
    () =>
      this.store.select(selectCompareBarProducts).pipe(
        concatLatestFrom(() => this.store.select(selectIsCompareHydrated)),
        filter(([, isCompareHydrated]) => isCompareHydrated === true),
        map(([compareBarProducts]) => compareBarProducts),
        distinctUntilChanged((prev, curr) => this.areSameCompareProducts(prev, curr)),
        tap((compareBarProducts) => {
          this.updateSessionStorage(compareBarProducts);
        }),
      ),
    { dispatch: false },
  );

  /**
   * @description
   * When product list is updated, get the availability information and set data in state
   */
  updateCompareProductAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(compareDrawerOpened, changeHeaderServiceStoreInfo),
      concatLatestFrom(() => [
        this.store.select(selectIsCompareGridOpen),
        this.store.select(selectCompareBarProducts),
        this.store.select(selectUserStoreInfo),
      ]),
      filter(
        ([, isGridOpen, compareBarProducts, _userSelectedStore]: [
          Action,
          boolean,
          CompareBarProduct[],
          HeaderStoreSelection | null,
        ]) => isGridOpen && compareBarProducts.length > 0,
      ),
      switchMap(
        ([, , compareBarProducts, userSelectedStore]: [
          Action,
          boolean,
          CompareBarProduct[],
          HeaderStoreSelection | null,
        ]) => {
          if (userSelectedStore === null) {
            return EMPTY;
          }
          return this.productAvailability(
            compareBarProducts.map((product) => product.ecode),
            userSelectedStore,
          );
        },
      ),
      filter((compareProductsAvailability: ProductAvailabilityCollection | null) => {
        return compareProductsAvailability !== null;
      }),
      map((compareProductsAvailability: ProductAvailabilityCollection) => {
        return compareProductsAvailabilityFetched({ compareProductsAvailability });
      }),
    ),
  );

  triggerFetchForCompareProductDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(compareDrawerOpened),
      concatLatestFrom(() => [
        this.store.select(selectCompareBarProducts),
        this.store.select(selectCompareProductDetails),
        this.store.select(selectProductListSEOIdentifier),
      ]),
      filter(
        ([, products, productDetails, _identifier]: [
          Action,
          CompareBarProduct[],
          CompareProductDetails | null,
          string | undefined,
        ]) => this.verifyWhetherThereAreNewCompareProducts(products, productDetails),
      ),
      switchMap(() => of(compareProductDetailsFetchTriggered())),
    ),
  );

  getCompareProductDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(compareProductDetailsFetchTriggered),
      concatLatestFrom(() => [
        this.store.select(selectCompareBarProducts),
        this.store.select(selectProductListSEOIdentifier),
      ]),
      switchMap(([, products, identifier]: [Action, CompareBarProduct[], string | undefined]) => {
        const isClearance = identifier?.toLowerCase().startsWith('clearance') ?? false;
        const ecodes = products.map((product) => product.ecode);
        return this.getProductDetailsFromApi(ecodes, isClearance);
      }),
    ),
  );

  /**
   * Retry the last product details fetch using the cached parameters.
   */
  retryFetchCompareProductDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(compareProductDetailsFetchRetryTriggered),
      switchMap(() => {
        if (!this.lastFetchParams) {
          return EMPTY;
        }
        // Extract the previously cached parameters
        const { ecodes, isClearance } = this.lastFetchParams;
        return this.getProductDetailsFromApi(ecodes, isClearance);
      }),
    ),
  );

  private productAvailability(
    ecodes: string[],
    store: HeaderStoreSelection,
  ): Observable<ProductAvailabilityCollection | null> {
    if (isPlatformServer(this.platformId) || !ecodes?.length) {
      return of(null);
    }

    return this.productCatalogService
      .getAvailabilityData(ecodes, true, store?.zipcode, store?.store)
      .pipe(catchError(() => of(null)));
  }

  private updateSessionStorage(compareProducts: CompareBarProduct[]): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (compareProducts.length === 0) {
      this.sessionStorageService.remove(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '');
      return;
    }

    this.sessionStorageService.set(
      COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY,
      JSON.stringify(compareProducts),
      '',
    );
  }

  private areSameCompareProducts(prev: CompareBarProduct[], curr: CompareBarProduct[]): boolean {
    if (prev === curr) {
      return true;
    }

    if (prev?.length !== curr?.length) {
      return false;
    }

    for (let i = 0; i < prev.length; i++) {
      if (prev[i]?.ecode !== curr[i]?.ecode) {
        return false;
      }
    }

    return true;
  }

  private verifyWhetherThereAreNewCompareProducts(
    products: CompareBarProduct[],
    compareProductDetails: CompareProductDetails | null,
  ): boolean {
    const ecodes = products.map((product) => product.ecode);
    const storeEcodes = compareProductDetails?.products?.map((product) => product.style.partNumber);

    const newEcodes = ecodes.filter((ecode: string) => !storeEcodes?.includes(ecode));

    return newEcodes.length > 0;
  }

  private getProductDetailsFromApi(ecodes: string[], isClearance: boolean): Observable<Action> {
    // Store the current parameters in case a retry is needed after a failed API call
    this.lastFetchParams = { ecodes, isClearance };

    // set the product details to state and loading state and error state
    return this.compareService.getProductDetails(ecodes, isClearance).pipe(
      map((productDetails: CompareProductDetails) =>
        compareProductDetailsFetchSucceeded({ productDetails }),
      ),
      catchError(() => of(compareProductDetailsFetchFailed())),
    );
  }
}
